import { type ReactNode } from "react";
import { cn } from "@/lib/utils";

// ── Card ─────────────────────────────────────

interface CardProps {
  children: ReactNode;
  className?: string;
  interactive?: boolean;
  onClick?: () => void;
  padding?: "none" | "sm" | "md" | "lg";
}

const paddingMap = {
  none: "",
  sm: "p-3",
  md: "p-5",
  lg: "p-6",
};

export function Card({
  children,
  className,
  interactive = false,
  onClick,
  padding = "md",
}: CardProps) {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      onClick={onClick}
      className={cn(
        "bg-white rounded-2xl border border-surface-border",
        interactive
          ? "shadow-card transition-all duration-200 hover:shadow-card-hover hover:border-dark-100 cursor-pointer"
          : "shadow-card",
        paddingMap[padding],
        onClick && "text-left w-full focus-ring",
        className
      )}
    >
      {children}
    </Component>
  );
}

// ── Badge ────────────────────────────────────

type BadgeVariant =
  | "default"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "pending"
  | "brand";

const badgeStyles: Record<BadgeVariant, string> = {
  default: "bg-surface-tertiary text-dark-500",
  success: "bg-status-success-bg text-status-success",
  warning: "bg-status-warning-bg text-status-warning",
  error: "bg-status-error-bg text-status-error",
  info: "bg-status-info-bg text-status-info",
  pending: "bg-status-pending-bg text-status-pending",
  brand: "bg-brand-50 text-brand-600",
};

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  className?: string;
  dot?: boolean;
}

export function Badge({
  children,
  variant = "default",
  className,
  dot = false,
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-caption font-medium whitespace-nowrap",
        badgeStyles[variant],
        className
      )}
    >
      {dot && (
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
      )}
      {children}
    </span>
  );
}

// ── Status Pill (trade/transaction status) ───

const statusVariantMap: Record<string, BadgeVariant> = {
  completed: "success",
  settled: "success",
  active: "success",
  approved: "success",
  future: "info",
  unsettled: "warning",
  pending: "pending",
  processing: "pending",
  under_review: "warning",
  pending_documents: "warning",
  failed: "error",
  cancelled: "error",
  closed: "default",
};

const statusLabelMap: Record<string, string> = {
  future: "Future",
  unsettled: "Unsettled",
  completed: "Completed",
  pending_documents: "Pending Docs",
  under_review: "Under Review",
  trade_settlement: "Settlement",
};

interface StatusPillProps {
  status: string;
  className?: string;
}

export function StatusPill({ status, className }: StatusPillProps) {
  const variant = statusVariantMap[status] ?? "default";
  const label =
    statusLabelMap[status] ??
    status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <Badge variant={variant} dot className={className}>
      {label}
    </Badge>
  );
}

// ── Metric ───────────────────────────────────

interface MetricProps {
  label: string;
  value: string;
  change?: number; // percentage, positive = up
  sublabel?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function Metric({
  label,
  value,
  change,
  sublabel,
  className,
  size = "md",
}: MetricProps) {
  const valueSize = {
    sm: "text-heading-sm",
    md: "text-display-sm",
    lg: "text-display-md",
  }[size];

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <span className="text-caption text-dark-300 font-medium uppercase tracking-wider">
        {label}
      </span>
      <span className={cn("font-display font-semibold text-dark-800", valueSize)}>
        {value}
      </span>
      {(change !== undefined || sublabel) && (
        <div className="flex items-center gap-2">
          {change !== undefined && (
            <span
              className={cn(
                "text-caption font-medium",
                change >= 0 ? "text-status-success" : "text-status-error"
              )}
            >
              {change >= 0 ? "↑" : "↓"} {Math.abs(change).toFixed(2)}%
            </span>
          )}
          {sublabel && (
            <span className="text-caption text-dark-300">{sublabel}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Skeleton ─────────────────────────────────

interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
}

export function Skeleton({ className, width, height }: SkeletonProps) {
  return (
    <div
      className={cn("skeleton", className)}
      style={{ width, height }}
    />
  );
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("card p-5 space-y-3", className)}>
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

// ── Empty State ──────────────────────────────

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-16 px-6 text-center",
        className
      )}
    >
      {icon && (
        <div className="w-12 h-12 rounded-2xl bg-surface-tertiary flex items-center justify-center text-dark-300 mb-4">
          {icon}
        </div>
      )}
      <h3 className="text-heading-sm text-dark-600 mb-1">{title}</h3>
      {description && (
        <p className="text-body-sm text-dark-300 max-w-sm">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
